import { ImageWDefault } from '@/components/image-w-default';
import { InviteFriendsModal } from '@/components/modals/invite-friends-modal';
import { NavButton } from '@/components/nav-button';
import { RestrictedWallet } from '@/components/restricted-wallet';
import { Tooltip } from '@/components/ui/tooltip';
import { AppRoute, TVL_TIERS } from '@/constants/shared';
import { useApps } from '@/hooks/useApps';
import { useRole } from '@/hooks/useRole';
import { useWalletAddressUserData } from '@/hooks/useWalletAddressUserData';
import {
  calculateFadeFromFocus,
  formatCurrencyToLocale,
  formatNumberToLocale,
  getHighestTvlTier,
  getIconPathFromAppId,
} from '@/utils';
import { Box, FormatNumber, Heading, HStack, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { BiDollar } from 'react-icons/bi';
import { GoPeople } from 'react-icons/go';
import { IoIosLink } from 'react-icons/io';
import { IoCheckmarkSharp } from 'react-icons/io5';
import { PiChartBar, PiFireSimple, PiLightning } from 'react-icons/pi';
import { CardWithToolip } from './CardWithTooltip';
import { PPCard } from './PPCard';
import { useProfileWalletAddress } from './useProfileWalletAddress';

export const PlumePointsSection = () => {
  const { profileAddress } = useProfileWalletAddress();
  const { stats } = useWalletAddressUserData(profileAddress);
  const { apps } = useApps();
  const { isAdmin } = useRole();
  const isRestricted = stats?.xpRank === null;

  // Simplified app click handler
  const handleAppClick = useCallback(
    (appId: string) => {
      const app = apps?.find((app) => app.id === appId);
      if (app?.website) {
        window.open(app.website, '_blank');
      }
    },
    [apps],
  );

  const appTitlesMap = useMemo(() => {
    if (!apps?.length) return {};

    return apps.reduce(
      (acc, app) => {
        acc[app.id] = app.title;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [apps]);

  const maxTiersToShow = 5;
  const highestTierEarned = getHighestTvlTier(stats?.TVL ?? 0);

  const visibleTvlTiers = useMemo(() => {
    const highestTierEarnedIndex = TVL_TIERS.findIndex((tvl) => tvl === highestTierEarned);
    const lowestTierToShowIdx = Math.max(0, highestTierEarnedIndex - 2);
    const highestTierToShowIdx = lowestTierToShowIdx + maxTiersToShow;
    const _visibleTvlTiers = TVL_TIERS.slice(lowestTierToShowIdx, highestTierToShowIdx);

    const focusIdx = _visibleTvlTiers.findIndex((tvl) => tvl === highestTierEarned);

    const visibleTvlTiersWithOpacity = calculateFadeFromFocus(_visibleTvlTiers, focusIdx);
    return visibleTvlTiersWithOpacity;
  }, [highestTierEarned]);

  if (!stats) return;

  return (
    <>
      <Heading fontFamily="Lufga, sans-serif" fontWeight={'700'} fontSize={'2xl'}>
        Plume Points
      </Heading>
      <Text color="fg.muted" fontSize={'xl'} fontWeight={'500'} fontFamily="Lufga, sans-serif">
        The more PP you earn, the higher your share of Plume's Airdrop Season 2.
      </Text>
      {(isAdmin || !isRestricted) && <PPCard address={profileAddress as `0x${string}`} />}
      <RestrictedWallet address={profileAddress!} />
      <SimpleGrid columns={[1, 1, 2]} gap={4}>
        <VStack gap={4}>
          <CardWithToolip
            icon={<BiDollar />}
            iconBgColor="#CDEFC4"
            iconColor="#4A783E"
            title="TVL | Daily Streak"
            titleTooltip="Total value locked by you on Plume | Longest daily streak in one of the TVL tiers"
            body={`${formatCurrencyToLocale(stats.TVL)} | ${stats.longestTvlStreak}`}
            slotBottom={
              <VStack mt={8} fontSize={'sm'}>
                {visibleTvlTiers.map(({ value, opacity }) => {
                  const isEarnedTvlLevel = value <= stats.TVL;
                  const isCurrentTvlLevel = value === highestTierEarned;
                  return (
                    <HStack
                      w="full"
                      key={value}
                      justifyContent="space-between"
                      borderTopWidth={value !== 1000 ? 'thin' : 'none'}
                      borderTopColor={value !== 1000 ? 'border' : 'none'}
                      pb={1}
                      pt={3}
                      opacity={opacity}
                    >
                      <Text fontWeight={500}>${formatNumberToLocale(value)}+</Text>
                      {isEarnedTvlLevel && !isCurrentTvlLevel && <IoCheckmarkSharp />}
                      {isCurrentTvlLevel && '🔥'}
                      {value > stats.TVL && <Text color="colorPalette.500">-</Text>}
                    </HStack>
                  );
                })}
              </VStack>
            }
            flex={3}
          />
          <CardWithToolip
            icon={<PiFireSimple />}
            iconBgColor="#FFDBC7"
            iconColor="#9A300E"
            title="Plume Staked | Current Daily Streak"
            titleTooltip="USD Value of Plume Staked | Current daily streak of staking Plume"
            slotRight={
              <NavButton href="https://staking.plumenetwork.xyz/" fontSize="sm">
                Stake Plume
              </NavButton>
            }
            body={
              <HStack>
                <FormatNumber value={stats.plumeStaked} maximumSignificantDigits={3} />
                <Text>|</Text>
                <Text>{stats.plumeStakingStreak}</Text>
              </HStack>
            }
          />
        </VStack>
        <VStack gap={4}>
          <CardWithToolip
            icon={<PiChartBar />}
            iconBgColor="#DDF8EF"
            iconColor="#236B53"
            title="Volume Generated"
            titleTooltip="Volume generated on the Plume network"
            body={`${formatCurrencyToLocale(stats.swapVolume)}`}
          />
          <CardWithToolip
            icon={<IoIosLink />}
            iconBgColor="#F5E5FF"
            iconColor="#7F539A"
            title="Trades | Longest Weekly Streak"
            titleTooltip="Number of trades you've performed on the Plume network | Longest weekly streak of trading"
            body={`${stats.swapCount} | ${stats.longestSwapStreakWeeks ?? 0}`}
          />
          <CardWithToolip
            icon={<GoPeople />}
            iconBgColor="#F3FBC3"
            iconColor="#5B6A2F"
            title="Referrals"
            titleTooltip="This shows your total referral count. You are awarded 10% of the Plume Points of your top 10 referrals"
            body={stats.referrals}
            slotRight={<InviteFriendsModal variant="outline" fontSize="sm" />}
          />
          <CardWithToolip
            iconBgColor="#FFF6C7"
            iconColor="#9A6A0E"
            icon={<PiLightning />}
            title="Daily Spin Streak"
            titleTooltip="Number of consecutive days you've spun the Daily Spin wheel"
            body={`${stats.dailySpinStreak}`}
            slotRight={
              <NavButton href="/daily-spin" fontSize="sm">
                Spin Now
              </NavButton>
            }
          />
        </VStack>
      </SimpleGrid>
      <Stack w="full" mt={4} gap={4} columns={[1, 1, 2]} flexFlow={['column-reverse', 'column-reverse', 'row']}>
        <SimpleGrid gap={4} columns={[1, 1, 2]} flex={1}>
          <CardWithToolip
            title="Most Used Apps"
            titleTooltip="These are the apps you interact with the most"
            body={
              <HStack gap={1} w="full">
                {/* BE currently returns empty strings instead of nully values for protocols 2 and 3 in mock data */}
                {[stats.protocol1, stats.protocol2, stats.protocol3].filter(Boolean).map((name) => {
                  return (
                    <Box key={name} onClick={() => handleAppClick(name)}>
                      <Tooltip content={appTitlesMap[name] || name} openDelay={100} closeDelay={100}>
                        <ImageWDefault
                          src={getIconPathFromAppId(name)}
                          defaultSrc={'/assets/apps/default-app-icon.avif'}
                          boxSize={8}
                        />
                      </Tooltip>
                    </Box>
                  );
                })}
              </HStack>
            }
            h="full"
          />
          <CardWithToolip
            title="Unique App Interactions"
            titleTooltip="How many Plume Portal Apps you've interacted with"
            body={stats.protocolsUsed}
            h="full"
          />
        </SimpleGrid>
        <CardWithToolip
          icon={<IoCheckmarkSharp />}
          iconBgColor="#D6F6FC"
          iconColor="#207B9B"
          title="Level Up"
          titleTooltip="How many quests you've completed"
          body={stats.completedQuests}
          slotRight={
            <NavButton href={AppRoute.Quests} fontSize="sm">
              Explore Level Up
            </NavButton>
          }
        />
      </Stack>
    </>
  );
};
